import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Button, TextInput, StackableCardDeck, StackableCard, DateOfBirthInput, MaskedInput, StandaloneAddressQuickSearchForm, OptionalBehindButton } from '@uplift/ui-v2';
import { Formik } from 'formik';
import { useAboutYouFormik } from './validation';
import { StickyButton } from '../../components';
import { ROUTING_CONFIG } from '@uplift/constants';
import { useL10n } from '@uplift/hooks';
import { useCurrentuser } from '@uplift/contexts';
import { useProviderDetailsQuery, useUserDisplayNameQuery } from '@uplift/queries';
import { useMemo } from 'react';

export const AboutYou: React.VFC = () => {
  const { classes, cx } = useStyles();
  const l10n = useL10n();
  const me = useCurrentuser();
  const { data: providerName, isLoading } = useUserDisplayNameQuery(me?.id, { enabled: !!me });
  const legalName = useMemo(() => {
    return isLoading || !providerName?.fullLegalName ? '...' : providerName.fullLegalName;
  }, [ isLoading, providerName ]);

  const { data: providerDetails } = useProviderDetailsQuery(me?.id, { enabled: !!me });
  const showPayorEnrollment = !providerDetails?.verifiableId;
  const nextRoute = showPayorEnrollment ? ROUTING_CONFIG.onboardingPayorEnrollment : ROUTING_CONFIG.main;

  const { aboutYouValidation, handleSubmit, initialValues } = useAboutYouFormik(me.id, nextRoute, { skipSetRegular: showPayorEnrollment });

  const hiddenPhone = cx('fs-exclude', classes.phone);

  return (
    <Box width="100%">
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={aboutYouValidation}
        isInitialValid={false}
      >
        {formik => (
          <Box display="flex" flexDirection="column" gap="16px">
            <Box px={{ xs: 1, sm: 0 }}>
              <Typography variant="h4">
                About You
              </Typography>
            </Box>
            <StackableCardDeck className={classes.deck}>
              <StackableCard>
                <TextInput
                  name="legalName"
                  label="Legal Name"
                  disabled
                  placeholder={legalName}
                  helperText="Need to change? Contact help@joinuplift.co"
                  className={classes.padding}
                />
                <TextInput
                  name="preferredName"
                  label="Preferred first name (Optional)"
                  helperText="Let us know the name you would prefer if it's different from your legal name"
                  fullWidth
                  color="secondary"
                  inputClasses={{
                    root: classes.name
                  }}
                />
              </StackableCard>
              <StackableCard>
                <DateOfBirthInput
                  name="dateOfBirth"
                  label="Date of Birth"
                  helperText="MM/DD/YYYY"
                  color="secondary"
                  inputClasses={{ root: classes.dob }}
                />
              </StackableCard>
            </StackableCardDeck>
            <Box px={{ xs: 1, sm: 0 }}>
              <Typography variant="h4">
                Contact Information
              </Typography>
            </Box>
            <StackableCardDeck className={classes.deck}>
              <StackableCard>
                <Box display="flex" flexDirection="column" gap="24px">
                  <Typography variant="body2" color="textPrimary">
                    <b>
                      What&apos;s the best way for the BetterHelp team to reach you?
                    </b>
                  </Typography>
                  <MaskedInput
                    name="mobilePhone"
                    label={l10n.matching.payment.mobilePhone}
                    type="phone"
                    className={hiddenPhone}
                    color="secondary"
                  />
                  <MaskedInput
                    name="workPhone"
                    label={l10n.settings.address.workPhone}
                    type="phone"
                    className={hiddenPhone}
                    color="secondary"
                  />
                  <OptionalBehindButton buttonText={l10n.matching.payment.addHomePhone}>
                    <MaskedInput
                      name="homePhone"
                      label={l10n.matching.payment.homePhone}
                      type="phone"
                      className={hiddenPhone}
                      color="secondary"
                    />
                  </OptionalBehindButton>
                </Box>
              </StackableCard>
              <StackableCard>
                <StandaloneAddressQuickSearchForm
                  label="Home Address"
                  getFieldName={name => `address.${name}`}
                />
              </StackableCard>
            </StackableCardDeck>
            <StickyButton>
              <Button
                variant="contained"
                color="secondary"
                disabled={!formik.isValid || formik.isSubmitting}
                onClick={formik.handleSubmit}
              >
                {showPayorEnrollment ? 'Next' : 'Finish setup'}
              </Button>
            </StickyButton>
          </Box>
        )}
      </Formik>
    </Box>
  );
};

const useStyles = makeStyles()(theme => ({
  deck: {
    marginBottom: theme.spacing(5),
  },
  padding: {
    paddingBottom: theme.spacing(2)
  },
  divider: {
    marginTop: theme.spacing(5),
  },
  dob: {
    width: '124px',
  },
  name: {
    [theme.breakpoints.up('sm')]: {
      width: '460px',
    }
  },
  phone: {
    width: '220px'
  }
}));
